import { useKinde } from '@/hooks/useKinde';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { type RootState } from '@/store';
import { isTauri } from '@/tauri/utils';
import {
  type AuthIdentityConfig,
  type AuthIdentityInputs,
  type IsAuthenticatedState,
  useIsAuthenticated,
} from '@heidi/auth/state';
import { externalLaunchTokenAtom } from '@heidi/integrations/ehr/data/atoms';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

// Re-export kept while production importers still read `useAuthToken` here.
// Exit: those importers repoint to `@/hooks/useAuthToken`, then this line goes.
export { useAuthToken } from './useAuthToken';

// Re-export kept so app importers and jest mocks stay on this module.
// Exit: they repoint to `@heidi/auth/state`, then these lines go.
export { useIsAuthenticated };
export type { IsAuthenticatedState };

/**
 * The store, Kinde and external-launch reads, in the order the hook
 * always took them. Kinde fields stay raw; the package coerces them.
 */
const useAuthIdentityInputs = (): AuthIdentityInputs => {
  const kinde = useKinde();
  const reduxProvider = useSelector((s: RootState) => s.auth.authProvider);
  const externalLaunchToken = useAtomValue(externalLaunchTokenAtom);
  const legacyAuthToken = useSelector((s: RootState) => s.auth.token);
  return {
    reduxProvider,
    legacyAuthToken,
    externalLaunchToken,
    kinde: {
      isAuthenticated: kinde.isAuthenticated,
      isLoading: kinde.isLoading,
    },
  };
};

/** The one object the app hands `configureAuthIdentity`. */
export const appAuthIdentity: AuthIdentityConfig = {
  useAuthIdentityInputs,
  getEffectiveAuthProvider,
  isTauri,
};
